/**
 * The studio's private book: each of the estate's own works with its costs,
 * values, tally and lifecycle, and the edit form that rewrites one record.
 *
 * Pure. Studio works go in; rows, drafts and a PUT-shaped request come out.
 * The copy is `estate.constants.ts`; the read, the open card and the save are
 * `useStudioController.ts`; registering a new work into the book is
 * `registerStudio.model.ts`. None of this reaches the collector-facing record.
 *
 * The lifecycle badge is derived at render from the produced count against
 * the edition and from whether a consignment holds the work — nothing stores
 * it, and a live consignment outranks the tally. A currency-less figure is
 * impossible by constraint, so the dash covers a missing figure and a missing
 * currency alike.
 */

import type { SetStudioRecordRequest, StudioWork } from "@ondo/shared";

import { money, parseMoneyMinor } from "../lib/money.ts";
import { STUDIO_COPY } from "./estate.constants.ts";

/**
 * The studio's book, and the lifecycle DERIVED at render.
 *
 * Nothing stores a lifecycle string — 20260911091000 decision 3. The words
 * come from the studio's own tally against the edition, and from whether a
 * live consignment holds the work; a mutable status column is exactly what
 * `works.verification` was dropped for being.
 */

export type Lifecycle = "conceived" | "in_production" | "produced" | "with_gallery";

export interface StudioRow {
  workId: string;
  title: string;
  /** '2025 · Painted bronze'. */
  meta: string;
  imagePath: string | null;
  href: string;
  lifecycle: Lifecycle | null;
  /** 'Edition 3/7 produced', when both numbers exist. */
  editionLine: string | null;
  /** Each a rendered figure or the pending dash. */
  cost: string;
  insured: string;
  retail: string;
  series: string;
  framing: string;
  location: string;
  consignedTo: string | null;
  /** The owner's own fact, derived server-side — see `StudioWork.neverSold`. */
  neverSold: boolean;
}

export type StudioView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; lead: string; detail: string }
  | { kind: "open"; summary: string; rows: StudioRow[] };

export function toStudioView(works: StudioWork[], workBase: string): StudioView {
  if (works.length === 0) {
    return { kind: "empty", lead: STUDIO_COPY.emptyLead, detail: STUDIO_COPY.emptyDetail };
  }

  return {
    kind: "open",
    summary: STUDIO_COPY.summary(works.length),
    rows: works.map((work) => toRow(work, workBase)),
  };
}

/**
 * The four states, in the order the screen's own footer promises: Conceived →
 * In production → Produced → With gallery. A live consignment outranks the
 * tally — a work at the gallery is with the gallery whatever the count says —
 * and a work whose studio has said nothing gets no badge rather than a guess.
 */
export function lifecycleOf(work: StudioWork): Lifecycle | null {
  if (work.consignedTo !== null) return "with_gallery";
  if (work.producedCount === null) return null;
  if (work.producedCount === 0) return "conceived";
  if (work.editionSize !== null && work.producedCount < work.editionSize) {
    return "in_production";
  }

  return "produced";
}

/** What the edit form holds: the record as typed, not yet parsed. */
export interface StudioDraft {
  cost: string;
  insured: string;
  retail: string;
  series: string;
  currency: string;
  framing: string;
  produced: string;
}

/** Minor units back to a typed figure — the inverse the form opens with. */
function typedFrom(minor: number | null): string {
  if (minor === null) return "";

  return (minor / 100).toFixed(minor % 100 === 0 ? 0 : 2);
}

export function draftFrom(work: StudioWork): StudioDraft {
  return {
    cost: typedFrom(work.costMinor),
    insured: typedFrom(work.insuredMinor),
    retail: typedFrom(work.retailMinor),
    series: typedFrom(work.seriesMinor),
    currency: work.currency ?? "GBP",
    framing: work.framingText ?? "",
    produced: work.producedCount === null ? "" : String(work.producedCount),
  };
}

/**
 * Why the draft cannot be saved, or null. The client-side half of the
 * `studio_money_names_its_currency` check — a mirror, not a second opinion;
 * the constraint still decides.
 */
export function draftRefusal(draft: StudioDraft): string | null {
  const figures = [draft.cost, draft.insured, draft.retail, draft.series];
  for (const typed of figures) {
    if (typed.trim() !== "" && parseMoneyMinor(typed) === null) {
      return STUDIO_COPY.badFigure;
    }
  }

  const anyFigure = figures.some((typed) => typed.trim() !== "");
  if (anyFigure && !/^[A-Za-z]{3}$/.test(draft.currency.trim())) {
    return STUDIO_COPY.needsCurrency;
  }

  if (draft.produced.trim() !== "" && !/^\d+$/.test(draft.produced.trim())) {
    return STUDIO_COPY.badCount;
  }

  return null;
}

/** The whole record, replaced — every field sent, blanks as nulls. */
export function toRequest(draft: StudioDraft): SetStudioRecordRequest {
  const figure = (typed: string) => (typed.trim() === "" ? null : parseMoneyMinor(typed));
  const anyFigure = [draft.cost, draft.insured, draft.retail, draft.series].some(
    (typed) => typed.trim() !== "",
  );

  return {
    costMinor: figure(draft.cost),
    insuredMinor: figure(draft.insured),
    retailMinor: figure(draft.retail),
    seriesMinor: figure(draft.series),
    currency: anyFigure ? draft.currency.trim().toUpperCase() : null,
    framingText: draft.framing.trim() === "" ? null : draft.framing.trim(),
    producedCount: draft.produced.trim() === "" ? null : Number(draft.produced.trim()),
  };
}

function toRow(work: StudioWork, workBase: string): StudioRow {
  const lifecycle = lifecycleOf(work);

  return {
    workId: work.workId,
    title: work.title,
    meta: [work.year, work.medium].filter(Boolean).join(" · "),
    imagePath: work.imagePath,
    href: `${workBase}/${work.workId}`,
    lifecycle,
    editionLine:
      work.editionSize !== null && work.producedCount !== null
        ? STUDIO_COPY.editionLine(work.producedCount, work.editionSize)
        : null,
    cost: figureOf(work.costMinor, work.currency),
    insured: figureOf(work.insuredMinor, work.currency),
    retail: figureOf(work.retailMinor, work.currency),
    series: figureOf(work.seriesMinor, work.currency),
    framing: work.framingText ?? STUDIO_COPY.none,
    location: work.locationText ?? STUDIO_COPY.none,
    consignedTo: work.consignedTo,
    neverSold: work.neverSold,
  };
}

/** A currency-less figure is impossible by constraint, so the dash covers both. */
function figureOf(minor: number | null, currency: string | null): string {
  if (minor === null || currency === null) return STUDIO_COPY.none;

  return money(minor, currency, STUDIO_COPY.none);
}
